import { useState, useCallback } from 'react';
import BulkActionService from '../services/BulkActionService';
import { ORDER_STATUS } from '../constants';
import { BulkActionOrder } from '../types';

interface BulkActionState {
  action: string;
  callback: (() => Promise<void>) | null;
  target: number;
  disableRetry: boolean;
}

const initialBulkActionState: BulkActionState = {
  action: '',
  callback: null,
  target: 0,
  disableRetry: false
};

export interface UseBulkActionsResult {
  bulkActionState: BulkActionState;
  processing: boolean;
  showBulkRetry: boolean;
  showBulkActions: boolean;
  bulkError: string | null;
  showBulkRetryDialog: (selectedModels: BulkActionOrder[], selectedFailed: number) => void;
  showBulkIgnoreDialog: (selectedModels: BulkActionOrder[], selectedFailed: number) => void;
  showBulkCancelDialog: (selectedModels: BulkActionOrder[]) => void;
  executeBulkAction: () => Promise<void>;
  retryOrders: (orders: BulkActionOrder[]) => Promise<void>;
  ignoreOrders: (orders: BulkActionOrder[]) => Promise<void>;
  cancelOrders: (orders: BulkActionOrder[]) => Promise<void>;
  toggleBulkActions: (show: boolean) => void;
  toggleBulkRetry: (show: boolean) => void;
  clearBulkError: () => void;
}

export function useBulkActions(): UseBulkActionsResult {
  const [bulkActionState, setBulkActionState] = useState<BulkActionState>(initialBulkActionState);
  const [processing, setProcessing] = useState(false);
  const [showBulkRetry, setShowBulkRetry] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Show bulk retry dialog
  const showBulkRetryDialog = useCallback((selectedModels: BulkActionOrder[], selectedFailed: number) => {
    const config = BulkActionService.getBulkActionConfig(
      'Retry',
      selectedModels.filter(m => m.status === ORDER_STATUS.FAILED),
      selectedFailed
    );

    setBulkActionState({
      action: config.action,
      callback: config.handler,
      target: config.target,
      disableRetry: false
    });
    setShowBulkActions(false);
    setShowBulkRetry(true);
  }, []);

  // Show bulk ignore dialog
  const showBulkIgnoreDialog = useCallback((selectedModels: BulkActionOrder[], selectedFailed: number) => {
    const config = BulkActionService.getBulkActionConfig(
      'Ignore',
      selectedModels.filter(m => m.status === ORDER_STATUS.FAILED),
      selectedFailed
    );

    setBulkActionState({
      action: config.action,
      callback: config.handler,
      target: config.target,
      disableRetry: false
    });
    setShowBulkActions(false);
    setShowBulkRetry(true);
  }, []);

  // Show bulk cancel dialog
  const showBulkCancelDialog = useCallback((selectedModels: BulkActionOrder[]) => {
    const config = BulkActionService.getBulkActionConfig(
      'Cancel',
      selectedModels,
      selectedModels.length
    );

    setBulkActionState({
      action: config.action,
      callback: config.handler,
      target: config.target,
      disableRetry: false
    });
    setShowBulkActions(false);
    setShowBulkRetry(true);
  }, []);

  // Execute bulk action
  const executeBulkAction = useCallback(async () => {
    if (!bulkActionState.callback) return;

    try {
      setProcessing(true);
      setBulkActionState(prev => ({ ...prev, disableRetry: true }));
      setBulkError(null);

      await bulkActionState.callback();

      // Close dialog after successful action
      setShowBulkRetry(false);
    } catch (error: any) {
      console.error('Error executing bulk action:', error);
      setBulkError(error.message);
    } finally {
      setProcessing(false);
      setBulkActionState(prev => ({ ...prev, disableRetry: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkActionState.callback]);

  // Retry orders
  const retryOrders = useCallback(async (orders: BulkActionOrder[]) => {
    try {
      setProcessing(true);
      setBulkError(null);
      await BulkActionService.retryOrders(orders);
    } catch (error: any) {
      console.error('Error retrying orders:', error);
      setBulkError(error.message);
      throw error;
    } finally {
      setProcessing(false);
    }
  }, []);

  // Ignore orders
  const ignoreOrders = useCallback(async (orders: BulkActionOrder[]) => {
    try {
      setProcessing(true);
      setBulkError(null);
      await BulkActionService.ignoreOrders(orders);
    } catch (error: any) {
      console.error('Error ignoring orders:', error);
      setBulkError(error.message);
      throw error;
    } finally {
      setProcessing(false);
    }
  }, []);

  // Cancel orders
  const cancelOrders = useCallback(async (orders: BulkActionOrder[]) => {
    try {
      setProcessing(true);
      setBulkError(null);
      await BulkActionService.cancelOrders(orders);
    } catch (error: any) {
      console.error('Error cancelling orders:', error);
      setBulkError(error.message);
      throw error;
    } finally {
      setProcessing(false);
    }
  }, []);

  // Toggle bulk actions dialog
  const toggleBulkActions = useCallback((show: boolean) => {
    setShowBulkActions(show);
  }, []);

  // Toggle bulk retry dialog
  const toggleBulkRetry = useCallback((show: boolean) => {
    setShowBulkRetry(show);
  }, []);

  // Clear bulk error
  const clearBulkError = useCallback(() => {
    setBulkError(null);
  }, []);

  return {
    bulkActionState,
    processing,
    showBulkRetry,
    showBulkActions,
    bulkError,
    showBulkRetryDialog,
    showBulkIgnoreDialog,
    showBulkCancelDialog,
    executeBulkAction,
    retryOrders,
    ignoreOrders,
    cancelOrders,
    toggleBulkActions,
    toggleBulkRetry,
    clearBulkError
  };
}